import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import salesOrderService from "@plannel/services/data/sales-order-service";
import customerService from "@plannel/services/data/customer-service";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";

import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import colorUtil from "../utils/color-util";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = colorUtil;
const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 5, 0);

const SalesOrder = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);

  const reduxState = reduxUtil.createObjectState(viewName, { extraItemCodes: extraItemCodesRef.current, extraCustomerCodes: extraCustomerCodesRef.current });
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const [period, setPeriod] = useState({ start: reduxViewState?.start ?? START_DATE, end: reduxViewState?.end ?? END_DATE });

  const [tPages, setTotalPages] = useState(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState(reduxViewState?.customers ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }
    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    }

    if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }
    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    }

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveSalesOrder = () => {
    const params = getRequestParams();
    salesOrderService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setSalesOrders(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reduxViewState) {
          reduxDispatch(reduxUtil.updateViewState({
            ...reduxState,
            ...reduxViewState,
            start: period?.start,
            end: period?.end,
            itemSearch: itemSearch.current,
            customerSearch: customerSearch.current,
            currentPage,
            pageSize,
            advancedFilters,
          }));
        } 
      
        DataState.initialize(gridRef.current?.api);
      });
  };

  const retrieveCustomers = () => {
    customerService.getAll()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
        if (reduxViewState) {
          reduxViewState.customers = results;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const removeSalesOrder = () => {
    const salesOrderId = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        salesOrderId.push(data.id);
      }
    );

    if (salesOrderId.length > 0) {
      salesOrderService.remove(salesOrderId)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveSalesOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveSalesOrder = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      for (const data of allStateData) {
        if (data.orderNum == null || data.orderNum === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("orderNum")}] ${t("MSG_FieldRequired")}` });
          return;
        }
        if (data.itemCd === null || data.itemCd === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("itemCd")}] ${t("MSG_FieldRequired")}` });
          return;
        }
        if (data.dueDate === null || data.dueDate === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("dueDate")}] ${t("MSG_FieldRequired")}` });
          return;
        }
      }
      salesOrderService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveSalesOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSalesOrder();
    } else if (action === "DELETE") {
      removeSalesOrder();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSalesOrder();
    }
  };

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: "",
      itemCd: "",
      dueDate: ""
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  // const customerKeyCreator = (params) => {
  //   return params.value.customerCd;
  // };

  const dropdownValueFormatter = (data) => {
    return data.value ? data.value.name : "";
  };

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const setItemCode = function (params) {
    params.data.item = { itemCd: params.newValue, name: "" };
    params.data.itemCd = params.newValue;
    return true;
  };

  const columnDefs = [
    { headerName: "orderNum", field: "orderNum", width: 200, cellClass: 'stringType', filterType: "string"  },
    { headerName: "itemCd", field: "item.itemCd", colId: "itemCd", valueSetter: setItemCode, width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "item.name", colId: "itemName", type: ["nonEditableColumn"], width: 280, filterType: "string" },
    { headerName: "customerCd", field: "customer.customerCd", colId: "customerCd", minWidth: 150, type: ["nonEditableColumn"], flex: 2, cellClass: 'stringType', filterType: "string" },
    {
      headerName: "customerName", field: "customer", colId: "customerName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      // keyCreator: customerKeyCreator,
      cellEditorParams: {
        valueFormatter: dropdownValueFormatter,
        values: customers
      },
      comparator: ((value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending)),
      minWidth: 150,
      flex: 2,
      filterType: "string",
    },
    { headerName: "dueDate", field: "dueDate",
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd"), width: 100, filterType: "date" },
    { headerName: "orderQty", field: "orderQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "orderAmt", field: "orderAmt", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (reduxViewState && gridLoading && period) {
      DataState.initialize(gridRef?.current?.api);
      retrieveSalesOrder();
    }

    return () => {
      setSalesOrders([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(customers) && gridLoading) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  useEffect(() => {
    if (gridLoading && reduxViewState?.start && reduxViewState?.end) {
      initPageToRetrieve();
    }

    return () => {
      setSalesOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, period]);

  useEffect(() => {
    setGridLoading(true);
    if (!reduxViewState?.customers) {
      retrieveCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef}/>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} extraCodesRef={extraCustomerCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={salesOrderService} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="purchase-budget-grid" className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={salesOrders}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SalesOrder);